import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  LocationOn,
  AccessTime,
  Search,
  FilterList,
  AttachMoney
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { tourService } from '../services/firebase';
import { Tour } from '../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const ToursPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    try {
      setLoading(true);
      setError(null);
      const toursData = await tourService.getTours();
      setTours(toursData);
    } catch (err) {
      console.error('Error fetching tours:', err);
      setError('Tur paketleri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Kategorileri turlardan çıkar
  const categories = Array.from(new Set(tours.map(tour => getTranslatedData(tour.category)).filter(Boolean)));

  // Filtreleme
  const filteredTours = tours.filter(tour => {
    const lang = i18n.language;
    const name = getTranslatedData(tour.name, lang);
    const description = getTranslatedData(tour.description, lang);
    const destination = getTranslatedData(tour.destination, lang);

    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         destination.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !selectedCategory || getTranslatedData(tour.category, lang) === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: 'var(--light-gray-white)'
      }}>
        <CircularProgress sx={{ color: 'var(--light-blue-cta)' }} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tur Paketleri - HatTravel</title>
        <meta name="description" content="HatTravel tur paketleri. Yurt içi ve yurt dışı turlar, uygun fiyatlar." />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh'
      }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '3rem', md: '4rem' },
                color: 'var(--navy-blue)',
                mb: 2
              }}
            >
              {t('nav.tours')}
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4, fontWeight: 300 }}>
              {filteredTours.length > 0
                ? `${filteredTours.length} tur paketi bulundu`
                : 'Uygun tur paketi bulunamadı'}
            </Typography>
          </Box>

          {/* Filters */}
          <Card sx={{ 
            mb: 6, 
            p: 4, 
            backgroundColor: 'white',
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
          }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder="Tur ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'var(--light-blue-cta)' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  minWidth: 280,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                  }
                }}
              />
              
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Kategori</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Kategori"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{
                    borderRadius: 3,
                  }}
                >
                  <MenuItem value="">{t('Tümü')}</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                sx={{ 
                  borderRadius: 3,
                  borderColor: 'var(--light-blue-cta)',
                  color: 'var(--light-blue-cta)',
                  '&:hover': {
                    borderColor: 'var(--cobalt-blue)',
                    backgroundColor: 'var(--cobalt-blue)',
                    color: 'white',
                  }
                }}
              >
                Filtreleri Temizle
              </Button>
            </Box>
          </Card>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          {/* Tours Grid */}
          {filteredTours.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
              gap: 4,
              pb: 6
            }}>
              {filteredTours.map((tour) => (
                <Card
                  key={tour.id}
                  component={Link}
                  to={`/tours/${tour.id}`}
                  sx={{
                    height: '100%',
                    textDecoration: 'none',
                    color: 'inherit',
                    backgroundColor: 'white',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      '& .tour-image': {
                        transform: 'scale(1.05)'
                      }
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height="250"
                      image={tour.images?.[0] || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80'}
                      alt={getTranslatedData(tour.name)}
                      className="tour-image"
                      sx={{
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        objectFit: 'cover'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 2
                    }}>
                      <Chip
                        label={`₺${tour.price?.toLocaleString() || '0'}`}
                        sx={{
                          backgroundColor: '#005BBD',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '1rem',
                          p: '12px 10px',
                          height: 'auto',
                          borderRadius: '12px'
                        }}
                      />
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography gutterBottom variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1.5, flexGrow: 1 }}>
                      {getTranslatedData(tour.name)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 2 }}>
                      <LocationOn fontSize="small" />
                      <Typography variant="body2">{getTranslatedData(tour.destination)}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                      <AccessTime fontSize="small" />
                      <Typography variant="body2">
                        {tour.duration} {t('days', { count: tour.duration })}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Card sx={{ 
              textAlign: 'center', 
              py: 8,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 4,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
            }}>
              <Typography variant="h5" color="#7f8c8d" gutterBottom sx={{ fontWeight: 300 }}>
                Aradığınız kriterlere uygun tur bulunamadı
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                sx={{ 
                  mt: 2,
                  borderRadius: 3,
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#5a6fd8',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)'
                  }
                }}
              >
                Filtreleri Temizle
              </Button>
            </Card>
          )}

          {/* Results Count */}
          <Box sx={{ textAlign: 'center', mt: 6, pb: 6 }}>
            <Typography variant="body1" color="#7f8c8d" sx={{ fontWeight: 300 }}>
              {filteredTours.length} tur paketi gösteriliyor
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ToursPage; 